import type { ActivityEvent, Lead, Membership, Stage, Trigger } from "../../lib/types";
import type { FinanceInvoice, FinanceReceipt, RevenueGoal } from "../finance/contracts";
import { civilDate, daysBetween } from "../notifications/feed";
import { shiftDay } from "../leads/list";
import { rangeDays, type DayRange } from "../home/metrics";

/**
 * Fórmulas da Análise, separadas da apresentação (AGENTS §5, §10).
 *
 * Cada função declara a unidade, o período e o denominador que a tela precisa
 * mostrar ao lado do número. Nenhuma delas prevê nada por modelo: o que a tela
 * chama de diagnóstico é uma leitura de regras sobre os registros — dizer que é
 * IA seria inventar capacidade (§3).
 */

function inRange(day: string, range: DayRange): boolean {
  return day >= range.from && day <= range.to;
}

const SEM_ORIGEM = "Sem origem";

export interface ChannelRow {
  channel: string;
  /** Leads criados no período com essa origem. */
  leads: number;
  /** Participações ganhas no período, pela data de fechamento. */
  wins: number;
  /** Recebido no período pelos leads dessa origem, em centavos. */
  revenueCents: number;
  /** `ganhos / leads` do período; nulo quando não houve lead. */
  conversion: number | null;
  /** Comparação da conversão com a janela anterior de mesma duração. */
  trend: "up" | "down" | "flat";
}

export interface ChannelInput {
  leads: readonly Lead[];
  memberships: readonly Membership[];
  receipts: readonly FinanceReceipt[];
  range: DayRange;
  previous: DayRange;
}

/**
 * Desempenho por origem do lead.
 *
 * Leads são contados pela data de criação; ganhos, pela data de fechamento da
 * participação; receita, pela data do recebimento. São três marcos diferentes
 * de propósito — misturá-los faria a conversão responder por um período que o
 * número não cobre (§10).
 */
export function channelPerformance({ leads, memberships, receipts, range, previous }: ChannelInput): ChannelRow[] {
  const sourceOf = new Map(leads.map((lead) => [lead.id, lead.source?.trim() || SEM_ORIGEM]));
  const rows = new Map<string, { leads: number; wins: number; revenueCents: number }>();
  const before = new Map<string, { leads: number; wins: number }>();

  const bucket = (map: Map<string, { leads: number; wins: number; revenueCents: number }>, channel: string) => {
    const current = map.get(channel) ?? { leads: 0, wins: 0, revenueCents: 0 };
    map.set(channel, current);
    return current;
  };

  for (const lead of leads) {
    if (lead.archived) continue;
    const channel = sourceOf.get(lead.id) ?? SEM_ORIGEM;
    const created = civilDate(lead.createdAt);
    if (inRange(created, range)) bucket(rows, channel).leads += 1;
    if (inRange(created, previous)) {
      const item = before.get(channel) ?? { leads: 0, wins: 0 };
      item.leads += 1;
      before.set(channel, item);
    }
  }

  for (const membership of memberships) {
    if (membership.status !== "won" || !membership.closedAt) continue;
    const channel = sourceOf.get(membership.leadId);
    if (!channel) continue;
    const closed = civilDate(membership.closedAt);
    if (inRange(closed, range)) bucket(rows, channel).wins += 1;
    if (inRange(closed, previous)) {
      const item = before.get(channel) ?? { leads: 0, wins: 0 };
      item.wins += 1;
      before.set(channel, item);
    }
  }

  for (const receipt of receipts) {
    if (!inRange(receipt.receivedOn, range)) continue;
    const channel = receipt.sourceSnapshot?.trim() || sourceOf.get(receipt.leadId);
    if (!channel) continue;
    bucket(rows, channel).revenueCents += receipt.amountCents;
  }

  return [...rows.entries()]
    .map(([channel, value]) => {
      const conversion = value.leads > 0 ? value.wins / value.leads : null;
      const past = before.get(channel);
      const pastConversion = past && past.leads > 0 ? past.wins / past.leads : null;
      const trend: ChannelRow["trend"] =
        conversion === null || pastConversion === null
          ? "flat"
          : conversion > pastConversion
            ? "up"
            : conversion < pastConversion
              ? "down"
              : "flat";
      return { channel, ...value, conversion, trend };
    })
    .sort((left, right) => right.revenueCents - left.revenueCents || right.leads - left.leads);
}

export interface MonthRevenue {
  /** Primeiro dia do mês, `AAAA-MM-01`. */
  month: string;
  label: string;
  cents: number;
  /** Meta atribuída ao mês, em centavos; nula quando não há meta cobrindo o mês. */
  goalCents: number | null;
}

/**
 * Recebido por mês e a meta mensal correspondente.
 *
 * A meta é declarada por período (o trimestre, no produto), então o valor
 * mensal é a meta dividida pelos meses que ela cobre — e o rótulo diz isso.
 */
export function monthlyRevenue(
  receipts: readonly FinanceReceipt[],
  goals: readonly RevenueGoal[],
  months: number,
  now: string,
): MonthRevenue[] {
  const today = civilDate(now);
  const [year, month] = today.split("-").map(Number);
  const buckets: MonthRevenue[] = [];
  for (let back = months - 1; back >= 0; back -= 1) {
    const date = new Date(Date.UTC(year, month - 1 - back, 1));
    const key = date.toISOString().slice(0, 7);
    buckets.push({
      month: `${key}-01`,
      // O rótulo lê a mesma data do balde, que é meia-noite UTC do dia 1º.
      // Formatar em São Paulo recuaria três horas e nomearia o mês anterior.
      label: new Intl.DateTimeFormat("pt-BR", { month: "short", timeZone: "UTC" }).format(date),
      cents: 0,
      goalCents: null,
    });
  }

  for (const receipt of receipts) {
    const bucket = buckets.find((item) => item.month.slice(0, 7) === receipt.receivedOn.slice(0, 7));
    if (bucket) bucket.cents += receipt.amountCents;
  }

  for (const bucket of buckets) {
    const goal = goals.find((item) => item.periodStart <= bucket.month && item.periodEnd >= bucket.month);
    if (!goal) continue;
    const span = Math.max(1, monthsBetween(goal.periodStart, goal.periodEnd));
    bucket.goalCents = Math.round(goal.amountCents / span);
  }

  return buckets;
}

function monthsBetween(from: string, to: string): number {
  const [fromYear, fromMonth] = from.split("-").map(Number);
  const [toYear, toMonth] = to.split("-").map(Number);
  return (toYear - fromYear) * 12 + (toMonth - fromMonth) + 1;
}

export interface ApproachRow {
  name: string;
  /** Participações ganhas entre as encerradas com esse gatilho. */
  ratio: number;
  wins: number;
  closed: number;
}

/**
 * Conversão por abordagem: o gatilho registrado no lead é a abordagem.
 *
 * O denominador são as participações **encerradas** no período — ganhas mais
 * perdidas —, porque comparar com quem ainda está em jogo mediria o tempo, não
 * o aproveitamento (§10).
 */
export function approachConversion(
  leads: readonly Lead[],
  memberships: readonly Membership[],
  triggers: readonly Trigger[],
  range: DayRange,
): ApproachRow[] {
  const nameOf = new Map(triggers.map((trigger) => [trigger.id, trigger.name]));
  const rows = new Map<string, { wins: number; closed: number }>();
  const leadById = new Map(leads.map((lead) => [lead.id, lead]));

  for (const membership of memberships) {
    if (membership.status === "active" || !membership.closedAt) continue;
    if (!inRange(civilDate(membership.closedAt), range)) continue;
    const lead = leadById.get(membership.leadId);
    if (!lead) continue;
    for (const triggerId of lead.triggerIds ?? []) {
      const name = nameOf.get(triggerId);
      if (!name) continue;
      const row = rows.get(name) ?? { wins: 0, closed: 0 };
      row.closed += 1;
      if (membership.status === "won") row.wins += 1;
      rows.set(name, row);
    }
  }

  return [...rows.entries()]
    .map(([name, value]) => ({ name, ...value, ratio: value.closed > 0 ? value.wins / value.closed : 0 }))
    .sort((left, right) => right.ratio - left.ratio || right.closed - left.closed);
}

/**
 * Aproveitamento acumulado por dia: `ganhas / (ganhas + perdidas)` até o dia.
 *
 * É acumulado de propósito — a taxa diária de uma carteira pequena oscila entre
 * 0% e 100% e não diz nada. O número do dia mais recente é o do período.
 */
export function utilizationSeries(memberships: readonly Membership[], range: DayRange): number[] {
  const days = rangeDays(range);
  const series: number[] = [];
  let wins = 0;
  let losses = 0;
  const closed = memberships
    .filter((membership) => membership.status !== "active" && membership.closedAt)
    .map((membership) => ({ day: civilDate(membership.closedAt!), won: membership.status === "won" }));

  for (let index = 0; index < days; index += 1) {
    const day = shiftDay(range.from, index);
    for (const item of closed) {
      if (item.day !== day) continue;
      if (item.won) wins += 1;
      else losses += 1;
    }
    series.push(wins + losses > 0 ? wins / (wins + losses) : 0);
  }
  return series;
}

export interface PaceChannel {
  channel: string;
  /** Recebido por dia da janela, em centavos, do mais antigo ao mais recente. */
  values: number[];
  totalCents: number;
}

/** Ritmo dos canais: recebimento por dia e por origem, dentro da janela. */
export function channelPace(
  receipts: readonly FinanceReceipt[],
  leads: readonly Lead[],
  range: DayRange,
  limit = 3,
): PaceChannel[] {
  const days = rangeDays(range);
  const sourceOf = new Map(leads.map((lead) => [lead.id, lead.source?.trim() || SEM_ORIGEM]));
  const rows = new Map<string, number[]>();

  for (const receipt of receipts) {
    if (!inRange(receipt.receivedOn, range)) continue;
    const channel = receipt.sourceSnapshot?.trim() || sourceOf.get(receipt.leadId) || SEM_ORIGEM;
    const index = daysBetween(range.from, receipt.receivedOn);
    if (index < 0 || index >= days) continue;
    const values = rows.get(channel) ?? Array.from({ length: days }, () => 0);
    values[index] += receipt.amountCents;
    rows.set(channel, values);
  }

  return [...rows.entries()]
    .map(([channel, values]) => ({ channel, values, totalCents: values.reduce((sum, value) => sum + value, 0) }))
    .sort((left, right) => right.totalCents - left.totalCents)
    .slice(0, limit);
}

export type GapSeverity = "high" | "mid" | "low";

export interface Gap {
  key: "vazamento" | "follow-up" | "origem";
  severity: GapSeverity;
  title: string;
  description: string;
  action: string;
}

export interface GapInput {
  funnelId: string;
  stages: readonly Stage[];
  memberships: readonly Membership[];
  leads: readonly Lead[];
  events: readonly ActivityEvent[];
  invoices: readonly FinanceInvoice[];
  receipts: readonly FinanceReceipt[];
  range: DayRange;
  now: string;
  /** Quantos dias sem interação já contam como proposta parada. */
  quietDays?: number;
}

/**
 * Leitura de regras sobre os registros do período.
 *
 * São três perguntas fixas — onde o funil mais vaza, quais desfechos ficaram
 * sem contato e qual origem rende acima da média —, respondidas por contagem.
 * Sem registro suficiente, a regra não inventa conclusão: ela sai da lista.
 */
export function analysisGaps({
  funnelId,
  stages,
  memberships,
  leads,
  events,
  range,
  now,
  quietDays = 5,
}: GapInput): Gap[] {
  const gaps: Gap[] = [];
  const ordered = stages
    .filter((stage) => stage.funnelId === funnelId && !stage.archived)
    .sort((left, right) => left.order - right.order);
  const leadById = new Map(leads.map((lead) => [lead.id, lead]));
  const rows = memberships.filter((membership) => {
    const lead = leadById.get(membership.leadId);
    return (
      membership.funnelId === funnelId &&
      lead &&
      !lead.archived &&
      inRange(civilDate(lead.createdAt), range)
    );
  });

  // 1. Maior vazamento entre etapas consecutivas.
  if (ordered.length >= 2 && rows.length >= 4) {
    const index = new Map(ordered.map((stage, position) => [stage.id, position]));
    const furthest = rows.map((membership) => {
      if (membership.status === "won") return ordered.length - 1;
      let position = index.get(membership.stageId) ?? 0;
      for (const event of events) {
        if (event.kind !== "stage" || event.membershipId !== membership.id || !event.stageId) continue;
        position = Math.max(position, index.get(event.stageId) ?? 0);
      }
      return position;
    });
    const reached = ordered.map((_, position) => furthest.filter((item) => item >= position).length);
    let worst = -1;
    let worstDrop = 0;
    for (let position = 0; position < reached.length - 1; position += 1) {
      if (reached[position] === 0) continue;
      const drop = 1 - reached[position + 1] / reached[position];
      if (drop > worstDrop) {
        worstDrop = drop;
        worst = position;
      }
    }
    if (worst >= 0 && worstDrop > 0) {
      gaps.push({
        key: "vazamento",
        severity: worstDrop >= 0.4 ? "high" : worstDrop >= 0.2 ? "mid" : "low",
        title: `${ordered[worst].name} → ${ordered[worst + 1].name} é o maior vazamento`,
        description: `${Math.round(worstDrop * 100)}% das participações que chegaram a ${ordered[worst].name} não avançaram para ${ordered[worst + 1].name} no período. São ${reached[worst] - reached[worst + 1]} de ${reached[worst]}.`,
        action: "Ver etapas do funil",
      });
    }
  }

  // 2. Desfechos sem contato recente antes de fechar.
  const today = civilDate(now);
  const parados = rows.filter((membership) => {
    if (membership.status !== "active") return false;
    const lead = leadById.get(membership.leadId);
    const marco = lead?.lastInteractionAt ?? lead?.createdAt;
    return Boolean(marco && daysBetween(civilDate(marco), today) >= quietDays);
  });
  if (parados.length > 0) {
    gaps.push({
      key: "follow-up",
      severity: parados.length >= rows.length / 2 ? "high" : parados.length >= 3 ? "mid" : "low",
      title: "Participações abertas sem contato recente",
      description: `${parados.length} de ${rows.length} participações do período estão há ${quietDays} dias ou mais sem interação real registrada. Interação é contato com o lead, não movimentação interna.`,
      action: "Abrir a lista filtrada",
    });
  }

  // 3. Origem que converte acima da média.
  const porOrigem = new Map<string, { wins: number; closed: number }>();
  for (const membership of rows) {
    if (membership.status === "active") continue;
    const source = leadById.get(membership.leadId)?.source?.trim() || SEM_ORIGEM;
    const row = porOrigem.get(source) ?? { wins: 0, closed: 0 };
    row.closed += 1;
    if (membership.status === "won") row.wins += 1;
    porOrigem.set(source, row);
  }
  const totalClosed = [...porOrigem.values()].reduce((sum, row) => sum + row.closed, 0);
  const totalWins = [...porOrigem.values()].reduce((sum, row) => sum + row.wins, 0);
  if (totalClosed >= 4 && totalWins > 0) {
    const media = totalWins / totalClosed;
    const melhor = [...porOrigem.entries()]
      .filter(([, row]) => row.closed >= 2)
      .map(([source, row]) => ({ source, ratio: row.wins / row.closed, ...row }))
      .sort((left, right) => right.ratio - left.ratio)[0];
    if (melhor && media > 0 && melhor.ratio > media) {
      gaps.push({
        key: "origem",
        severity: "low",
        title: `${melhor.source} converte ${(melhor.ratio / media).toFixed(1).replace(".", ",")}× a média`,
        description: `${melhor.wins} de ${melhor.closed} participações encerradas com essa origem foram ganhas, contra ${Math.round(media * 100)}% no total do período.`,
        action: "Filtrar por essa origem",
      });
    }
  }

  return gaps;
}

/**
 * Relatório da Análise em CSV.
 *
 * Cada bloco vira uma seção com o próprio cabeçalho, porque a tela também
 * mostra três recortes diferentes. Células iniciadas por sinal viram texto
 * literal: sem isso a planilha executaria o conteúdo como fórmula (§26.3).
 */
export function analysisCsv(input: {
  period: string;
  funnel: string;
  channels: readonly ChannelRow[];
  months: readonly MonthRevenue[];
  approaches: readonly ApproachRow[];
  money: (cents: number) => string;
}): string {
  const neutralize = (value: unknown) => {
    const text = String(value ?? "");
    return /^[=+\-@\t\r]/.test(text) ? `'${text}` : text;
  };
  const cell = (value: unknown) => `"${neutralize(value).replaceAll('"', '""')}"`;
  const line = (values: unknown[]) => values.map(cell).join(";");
  const percent = (ratio: number | null) =>
    ratio === null ? "" : `${(ratio * 100).toFixed(1).replace(".", ",")}%`;

  const blocks = [
    [line(["Análise", input.period, input.funnel])],
    [""],
    [line(["Canal", "Leads", "Ganhos", "Receita", "Conversão"])],
    input.channels.map((row) =>
      line([row.channel, row.leads, row.wins, input.money(row.revenueCents), percent(row.conversion)]),
    ),
    [""],
    [line(["Mês", "Recebido", "Meta"])],
    input.months.map((month) =>
      line([month.label, input.money(month.cents), month.goalCents === null ? "" : input.money(month.goalCents)]),
    ),
    [""],
    [line(["Abordagem", "Ganhas", "Encerradas", "Conversão"])],
    input.approaches.map((row) => line([row.name, row.wins, row.closed, percent(row.ratio)])),
  ];

  return `\uFEFF${blocks.flat().join("\r\n")}`;
}

/**
 * Média do décimo superior por valor.
 *
 * O bloco de LTV mostra média e mediana lado a lado porque uma carteira com um
 * contrato grande tem as duas bem distantes; o topo 10% explica essa distância.
 * Com menos de dez clientes o décimo é o maior deles — e continua sendo uma
 * descrição honesta do topo, não uma extrapolação.
 */
export function topDecileCents(values: readonly { cents: number }[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].map((value) => value.cents).sort((left, right) => right - left);
  const size = Math.max(1, Math.round(sorted.length / 10));
  const top = sorted.slice(0, size);
  return Math.round(top.reduce((sum, value) => sum + value, 0) / top.length);
}

/**
 * Tempo médio, em meses, desde que cada cliente virou cliente.
 *
 * O marco é `clientSince` — a data do recebimento que fechou a venda —, não a
 * criação do lead: o que a carteira mede é o tempo de relação paga.
 */
export function clientTenureMonths(leads: readonly Lead[], now: string): number | null {
  const today = civilDate(now);
  const spans = leads
    .filter((lead) => Boolean(lead.clientSince) && !lead.archived)
    // `clientSince` já é um dia civil (coluna `date`): passar por `civilDate`
    // o converteria como se fosse meia-noite UTC e o jogaria um dia para trás.
    .map((lead) => daysBetween(lead.clientSince!, today))
    .filter((days) => days >= 0);
  if (spans.length === 0) return null;
  const average = spans.reduce((sum, days) => sum + days, 0) / spans.length;
  return average / 30.4375;
}
